import { Link } from "react-router-dom";
import { ArrowUpRight, Wheat, Droplets, Flame, Sparkles, ClipboardCheck, PackageCheck } from "lucide-react";
import { FadeUp, SectionHead } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import { IMAGES } from "@/data/site";

const PROCESS = [
  { icon: Wheat, title: "Sourcing", text: "Carefully selected gram flour, dals, peanuts and grains from trusted regional suppliers." },
  { icon: Sparkles, title: "Spice Blending", text: "Pure traditional spices ground and blended to the family's original formulations." },
  { icon: Flame, title: "Frying & Roasting", text: "Cooked in premium edible oil under controlled temperatures for consistent golden crunch." },
  { icon: Droplets, title: "Seasoning", text: "Each batch seasoned while warm so the masala binds evenly — the Waghe signature taste." },
  { icon: ClipboardCheck, title: "Quality Check", text: "Batch-wise checks for taste, texture, moisture and hygiene before anything leaves the floor." },
  { icon: PackageCheck, title: "Packing & Dispatch", text: "Sealed in freshness-lock retail, bulk and export-grade packs, dispatched across India and worldwide." },
];

const PILLARS = [
  { title: "Selected Ingredients", text: "Only graded flours, dals and nuts enter our production floor." },
  { title: "Premium Edible Oil", text: "Quality oil, monitored and replaced on strict schedules — never overused." },
  { title: "Pure Traditional Spices", text: "No shortcuts: authentic spice blends prepared the traditional way." },
  { title: "Hygienic Production", text: "50+ trained workers, clean-floor discipline and batch-wise quality records." },
];

export default function Manufacturing() {
  return (
    <>
      <section className="relative pt-44 pb-28 sm:pb-36 overflow-hidden" data-testid="manufacturing-hero">
        <img src={IMAGES.factory} alt="Waghe & Sons modern food production facility" className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/70 to-ink" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="text-[11px] sm:text-xs uppercase tracking-overline text-gold mb-6">Manufacturing &amp; Quality · Barwani, MP</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[1.02] text-cream max-w-4xl">
              Crafted with pure spices<br />&amp; <em className="text-gold italic">premium oil.</em>
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-7 max-w-2xl text-base md:text-lg text-zinc-300 font-light leading-relaxed">
              A modern production floor running on sixty-year-old recipes — where traditional flavour meets
              disciplined, hygienic, export-grade manufacturing.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-ink" data-testid="manufacturing-process">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead testId="process-head" overline="The Process" title="Six steps between the farm and your shelf." />
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {PROCESS.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.07} className="group bg-ink p-8 sm:p-10 hover:bg-coal transition-colors duration-500">
                <div className="flex items-start justify-between">
                  <s.icon className="w-7 h-7 text-gold" strokeWidth={1.5} />
                  <span className="font-display text-3xl text-outline-faint group-hover:text-outline-gold transition-colors duration-500">0{i + 1}</span>
                </div>
                <h3 className="mt-6 font-display text-xl text-cream">{s.title}</h3>
                <p className="mt-3 text-sm text-zinc-400 font-light leading-relaxed">{s.text}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-coal border-y border-white/10" data-testid="manufacturing-floor">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead testId="floor-head" overline="Inside the Facility" title="A floor built for consistency." />
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { img: IMAGES.factory, alt: "Production hall", cls: "col-span-2 row-span-2 aspect-[4/3] lg:aspect-auto" },
              { img: IMAGES.factoryHands, alt: "Hygienic food handling", cls: "aspect-square" },
              { img: IMAGES.factoryLine, alt: "Sorting & quality line", cls: "aspect-square" },
              { img: IMAGES.factoryWorker, alt: "Packaging quality control", cls: "aspect-square" },
              { img: IMAGES.factoryTeam, alt: "Production team at work", cls: "aspect-square" },
            ].map((f, i) => (
              <FadeUp key={f.alt} delay={i * 0.08} className={f.cls}>
                <div className="group relative w-full h-full overflow-hidden rounded-sm border border-white/10">
                  <img src={f.img} alt={f.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-108" style={{ transform: "scale(1.01)" }} />
                  <div className="absolute inset-0 bg-ink/25 group-hover:bg-ink/0 transition-colors duration-500" />
                  <p className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.2em] text-cream/90 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full">{f.alt}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-ink" data-testid="manufacturing-pillars">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div className="lg:sticky lg:top-32">
              <SectionHead
                testId="pillars-head"
                overline="Quality Pillars"
                title="Why a Waghe pack tastes the same, every single time."
              />
              <FadeUp delay={0.2}>
                <Link to="/export-inquiry" data-testid="manufacturing-cta" className="mt-9 inline-flex items-center gap-2.5 bg-gold text-ink text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(212,175,55,0.35)]">
                  Discuss Bulk Supply <ArrowUpRight className="w-4 h-4" />
                </Link>
              </FadeUp>
            </div>
            <div className="space-y-px bg-white/10 border border-white/10">
              {PILLARS.map((p, i) => (
                <FadeUp key={p.title} delay={i * 0.08} className="group bg-ink p-8 hover:bg-coal transition-colors duration-500 flex gap-6">
                  <span className="font-display text-4xl text-outline-gold group-hover:text-gold transition-colors duration-500 shrink-0">0{i + 1}</span>
                  <div>
                    <h3 className="font-display text-xl text-cream">{p.title}</h3>
                    <p className="mt-2 text-sm text-zinc-400 font-light leading-relaxed">{p.text}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>
      <EditorialMarquee />
    </>
  );
}
